import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineChatBubbleLeftRight, HiOutlineArrowLeft, HiOutlinePlus,
  HiOutlineLockClosed, HiOutlineXMark, HiOutlinePaperAirplane,
  HiOutlineTrash, HiOutlineFlag, HiOutlineSparkles,
} from "react-icons/hi2";
import { Navigate } from "react-router-dom";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import toast from "react-hot-toast";
import "./BoardPage.css";

const timeAgo = (d) => {
  if (!d) return "";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const CATEGORY_COLOR = {
  "Veteran": "#b85c38",
  "First Responder": "#2563eb",
  "Law Enforcement": "#7c3aed",
  "Gold Star Family": "#d97706",
};

/* ── New post composer ── */
function NewPostModal({ categoryId, categoryName, onClose, onPosted }) {
  const [title, setTitle] = useState("");
  const [body,  setBody]  = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handlePost = async () => {
    if (!title.trim()) { setError("Give your post a title."); return; }
    if (!body.trim())  { setError("Write something before posting."); return; }
    setSaving(true); setError("");
    try {
      await portalAPI.board.createPost(categoryId, { title: title.trim(), body: body.trim() });
      toast.success("Posted.");
      onPosted();
    } catch (err) {
      setError(err.message || "Couldn't post that.");
    } finally { setSaving(false); }
  };

  return (
    <div className="bd__overlay" onClick={onClose}>
      <div className="bd__modal" onClick={e => e.stopPropagation()}>
        <div className="bd__modal-head">
          <div>
            <h3>New Post</h3>
            <p className="bd__modal-sub">in {categoryName}</p>
          </div>
          <button type="button" onClick={onClose}><HiOutlineXMark /></button>
        </div>

        <input className="bd__input" placeholder="What's this about?"
          value={title} maxLength={200}
          onChange={e => { setTitle(e.target.value); setError(""); }} />

        <textarea className="bd__input bd__textarea" rows={7}
          placeholder="Share what's on your mind…"
          value={body} onChange={e => { setBody(e.target.value); setError(""); }} />

        {error && <p className="bd__error">{error}</p>}

        <div className="bd__modal-actions">
          <button type="button" className="p-btn p-btn--outline" onClick={onClose}>Cancel</button>
          <button type="button" className="p-btn p-btn--fill" disabled={saving} onClick={handlePost}>
            {saving ? "Posting…" : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Single thread view ── */
function ThreadView({ postId, onBack, onDeleted }) {
  const [post,    setPost]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState("");
  const [sending, setSending] = useState(false);

  const fetchPost = useCallback(() => {
    setLoading(true);
    portalAPI.board.post(postId)
      .then(res => setPost(res?.data || null))
      .catch(err => toast.error(err.message || "Couldn't load that post."))
      .finally(() => setLoading(false));
  }, [postId]);

  useEffect(() => { fetchPost(); }, [fetchPost]);

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await portalAPI.board.reply(postId, reply.trim());
      setReply("");
      fetchPost();
    } catch (err) { toast.error(err.message || "Couldn't send that."); }
    finally { setSending(false); }
  };

  const handleDeletePost = () => {
    toast((t) => (
      <div style={{ display:"flex", flexDirection:"column", gap:"0.5rem" }}>
        <p style={{ margin:0, fontWeight:700 }}>Delete this post?</p>
        <p style={{ margin:0, fontSize:"0.78rem", color:"#6b6258" }}>This can't be undone.</p>
        <div style={{ display:"flex", gap:"0.5rem" }}>
          <button onClick={async () => {
            toast.dismiss(t.id);
            try { await portalAPI.board.deletePost(postId); toast.success("Deleted."); onDeleted(); }
            catch (err) { toast.error(err.message || "Failed."); }
          }} style={{ background:"#dc2626", color:"#fff", border:"none", borderRadius:"5px", padding:"0.35rem 0.75rem", cursor:"pointer", fontWeight:700, fontSize:"0.78rem" }}>Delete</button>
          <button onClick={() => toast.dismiss(t.id)}
            style={{ background:"#f3f4f0", border:"none", borderRadius:"5px", padding:"0.35rem 0.75rem", cursor:"pointer", fontSize:"0.78rem" }}>Cancel</button>
        </div>
      </div>
    ), { duration: Infinity, style: { maxWidth:"300px" } });
  };

  const handleReport = async (payload) => {
    try {
      await portalAPI.board.report(payload);
      toast.success("Thanks — our team will take a look.");
    } catch (err) { toast.error(err.message || "Couldn't send that report."); }
  };

  const handleDeleteReply = async (replyId) => {
    try { await portalAPI.board.deleteReply(replyId); fetchPost(); }
    catch (err) { toast.error(err.message || "Failed."); }
  };

  if (loading) return <div className="bd__loading"><div className="bd__spinner" /></div>;
  if (!post) return (
    <div className="pc bd__empty">
      <p className="bd__empty-title">Post not found</p>
      <button type="button" className="p-btn p-btn--outline" onClick={onBack}>Back</button>
    </div>
  );

  return (
    <div className="bd__thread">
      <button type="button" className="bd__back" onClick={onBack}>
        <HiOutlineArrowLeft /> {post.categoryName}
      </button>

      <div className="pc bd__post">
        <div className="bd__post-head">
          <div className="bd__avatar" style={{ background: CATEGORY_COLOR[post.author.category] || "#6b6258" }}>
            {post.author.initials}
          </div>
          <div className="bd__post-who">
            <p className="bd__post-author">{post.author.name}</p>
            <p className="bd__post-time">{post.author.category ? `${post.author.category} · ` : ""}{timeAgo(post.createdAt)}</p>
          </div>
          <div className="bd__post-tools">
            {post.isMine ? (
              <button type="button" className="bd__tool" title="Delete post" onClick={handleDeletePost}>
                <HiOutlineTrash />
              </button>
            ) : (
              <button type="button" className="bd__tool" title="Report post"
                onClick={() => handleReport({ post_id: post.id })}>
                <HiOutlineFlag />
              </button>
            )}
          </div>
        </div>

        <h2 className="bd__post-title">{post.title}</h2>
        <p className="bd__post-body">{post.body}</p>
      </div>

      <div className="bd__replies-head">
        <p className="p-section-title" style={{ margin:0 }}>
          {post.replies.length} {post.replies.length === 1 ? "Reply" : "Replies"}
        </p>
      </div>

      {post.replies.length > 0 && (
        <div className="bd__replies">
          {post.replies.map(r => (
            <div key={r.id} className="pc bd__reply">
              <div className="bd__avatar bd__avatar--sm"
                style={{ background: CATEGORY_COLOR[r.author.category] || "#6b6258" }}>
                {r.author.initials}
              </div>
              <div className="bd__reply-body">
                <div className="bd__reply-meta">
                  <span className="bd__reply-author">{r.author.name}</span>
                  <span className="bd__reply-time">{timeAgo(r.createdAt)}</span>
                  {r.isMine ? (
                    <button type="button" className="bd__tool bd__tool--sm" title="Delete reply"
                      onClick={() => handleDeleteReply(r.id)}><HiOutlineTrash /></button>
                  ) : (
                    <button type="button" className="bd__tool bd__tool--sm" title="Report reply"
                      onClick={() => handleReport({ reply_id: r.id })}><HiOutlineFlag /></button>
                  )}
                </div>
                <p className="bd__reply-text">{r.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {post.isLocked ? (
        <div className="pc bd__locked">
          <HiOutlineLockClosed /> This conversation has been closed to new replies.
        </div>
      ) : (
        <div className="pc bd__composer">
          <textarea className="bd__input bd__textarea" rows={3}
            placeholder="Write a reply…"
            value={reply} onChange={e => setReply(e.target.value)} />
          <div className="bd__composer-foot">
            <button type="button" className="p-btn p-btn--fill"
              disabled={sending || !reply.trim()} onClick={handleReply}>
              <HiOutlinePaperAirplane /> {sending ? "Sending…" : "Reply"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Post list for one category ── */
function CategoryView({ category, onBack, onOpenPost }) {
  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    portalAPI.board.posts(category.id)
      .then(res => setPosts(res?.data?.posts || []))
      .catch(err => toast.error(err.message || "Couldn't load posts."))
      .finally(() => setLoading(false));
  }, [category.id]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  return (
    <div>
      <button type="button" className="bd__back" onClick={onBack}>
        <HiOutlineArrowLeft /> All Categories
      </button>

      <div className="bd__cat-head">
        <div>
          <h2 className="bd__cat-title">{category.name}</h2>
          {category.description && <p className="bd__cat-sub">{category.description}</p>}
        </div>
        <button type="button" className="p-btn p-btn--fill" onClick={() => setComposing(true)}>
          <HiOutlinePlus /> New Post
        </button>
      </div>

      {loading ? (
        <div className="bd__loading"><div className="bd__spinner" /></div>
      ) : posts.length === 0 ? (
        <div className="pc bd__empty">
          <HiOutlineChatBubbleLeftRight className="bd__empty-icon" />
          <p className="bd__empty-title">Nothing here yet</p>
          <p className="bd__empty-sub">Be the first to start a conversation in {category.name}.</p>
          <button type="button" className="p-btn p-btn--fill" onClick={() => setComposing(true)}>
            <HiOutlinePlus /> Start a Post
          </button>
        </div>
      ) : (
        <div className="bd__post-list">
          {posts.map(p => (
            <div key={p.id} className="pc bd__post-row" onClick={() => onOpenPost(p.id)}>
              <div className="bd__avatar" style={{ background: CATEGORY_COLOR[p.author.category] || "#6b6258" }}>
                {p.author.initials}
              </div>
              <div className="bd__post-row-main">
                <p className="bd__post-row-title">
                  {p.isPinned && <HiOutlineSparkles className="bd__pin" title="Pinned" />}
                  {p.title}
                  {p.isLocked && <HiOutlineLockClosed className="bd__lock" title="Closed" />}
                </p>
                <p className="bd__post-row-excerpt">{p.excerpt}{p.excerpt.length >= 180 ? "…" : ""}</p>
                <p className="bd__post-row-meta">
                  {p.author.name} · {timeAgo(p.createdAt)}
                  {p.replyCount > 0 && ` · ${p.replyCount} ${p.replyCount === 1 ? "reply" : "replies"}`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {composing && (
        <NewPostModal categoryId={category.id} categoryName={category.name}
          onClose={() => setComposing(false)}
          onPosted={() => { setComposing(false); fetchPosts(); }} />
      )}
    </div>
  );
}

/* ── Page ── */
export default function BoardPage() {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [category,   setCategory]   = useState(null);
  const [postId,     setPostId]     = useState(null);

  const [moved,      setMoved]      = useState(false);

  // When the Discourse forum is connected this board is a staff-only backup,
  // so members go to the Community page instead. Only redirect on a positive
  // answer; the Community page sends members back here when it isn't.
  useEffect(() => {
    let alive = true;
    portalAPI.community.info()
      .then((res) => res?.data?.enabled)
      .catch(() => false)
      .then((forumOn) => {
        if (!alive) return;
        if (forumOn) { setMoved(true); return; }
        return portalAPI.board.categories()
          .then(res => alive && setCategories(res?.data?.categories || []))
          .catch(() => alive && setCategories([]))
          .finally(() => alive && setLoading(false));
      });
    return () => { alive = false; };
  }, []);

  if (moved) return <Navigate to="/portal/community" replace />;

  return (
    <PortalLayout eyebrow="Community" pageTitle="Message Board"
      quote="We rise by lifting others." quoteAuthor="Forge Retreat">

      {postId ? (
        <ThreadView postId={postId} onBack={() => setPostId(null)}
          onDeleted={() => setPostId(null)} />
      ) : category ? (
        <CategoryView category={category} onBack={() => setCategory(null)}
          onOpenPost={setPostId} />
      ) : loading ? (
        <div className="bd__loading"><div className="bd__spinner" /></div>
      ) : (
        <>
          <p className="bd__intro">
            A space to ask questions, share what you're working through, and stay
            connected with people who understand.
          </p>
          <div className="bd__cat-grid">
            {categories.map(c => (
              <div key={c.id}
                className={`pc bd__cat-card ${c.locked ? "bd__cat-card--locked" : ""}`}
                onClick={() => !c.locked && setCategory(c)}>
                <div className="bd__cat-card-head">
                  <p className="bd__cat-card-name">{c.name}</p>
                  {c.locked && <HiOutlineLockClosed className="bd__cat-lock" />}
                </div>
                {c.description && <p className="bd__cat-card-sub">{c.description}</p>}
                <p className="bd__cat-card-meta">
                  {c.locked
                    ? "Opens up after your first retreat"
                    : `${c.post_count} ${c.post_count === 1 ? "post" : "posts"}${c.last_activity ? ` · active ${timeAgo(c.last_activity)}` : ""}`}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </PortalLayout>
  );
}
